import { CSSProperties } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';

import { TCellProps } from './TCell.props';

import styles from './TCell.module.css';

export default function TCell<T>({
  cell,
  className = '',
  getCellProps,
  onCellClickHandler,
  isActive,
  onCellClick,
  rowHeight = 1,
  isFirstDataColumn = false,
}: TCellProps<T>) {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  // Calculate dynamic height based on rowHeight multiplier
  const baseHeight = 32; // Base height from CSS
  const padding = 8; // 4px top + 4px bottom
  const calculatedHeight = baseHeight * rowHeight;
  const calculatedLineHeight = calculatedHeight - padding;

  const isColumnResizing =
    cell.getContext().table.getState().columnSizingInfo.isResizingColumn !== false;

  const style: CSSProperties = {
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: isColumnResizing ? 'none' : 'width transform 0.2s ease-in-out',
    width: cell.column.getSize(),
    zIndex: isDragging ? 1 : 0,
    height: calculatedHeight,
    lineHeight: `${calculatedLineHeight}px`,
  };

  const handleCellClick = () => {
    // Call the existing cell click handler for internal state
    onCellClickHandler(cell);

    // Call the custom cell click handler if provided
    if (onCellClick) {
      onCellClick(cell, cell.row, cell.row.original);
    }

    // Call the original onClick if it exists
    if (typeof getCellProps(cell).onClick === 'function') {
      getCellProps(cell).onClick();
    }
  };

  return (
    <div
      {...{
        ...getCellProps(cell),
        style: {
          ...style,
          ...(getCellProps(cell)?.style || {}),
        },
        ref: setNodeRef,
        className: cn(
          styles.cell,
          className,
          {
            [styles.active]: isActive === true,
            [styles.firstDataColumn]: isFirstDataColumn,
          },
          getCellProps(cell)?.className || ''
        ),
        onClick: handleCellClick,
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );
}
